import sanitizeHtml from 'sanitize-html';

import { fullAllowedHtmlTags, removeInvalidColspans } from './convert';

export interface SanitizePastedHTMLOptions {
  /**
   * Prepare images for the editor to upload:
   * - Keeps inline `data:` images with Froala's paste marker so they get uploaded.
   * - Drops other images without a `data-file-id` (remote, foreign `blob:`) — preview can't resolve them.
   * - Unmarks images that already have a `data-file-id` so they aren't re-uploaded.
   */
  keepPastedImages?: boolean;
}

const PASTED_IMAGE_MARKER = 'data-fr-image-pasted';
const UPLOADABLE_SRC = /^data:/i;

const pastedImageOptions: sanitizeHtml.IOptions = {
  allowedAttributes: { ...fullAllowedHtmlTags, img: [...fullAllowedHtmlTags.img, PASTED_IMAGE_MARKER] },
  allowedSchemesByTag: { img: ['http', 'https', 'data', 'blob'] },
  transformTags: {
    img: (tagName, attribs) => {
      if (!attribs['data-file-id']) return { tagName, attribs };
      const { [PASTED_IMAGE_MARKER]: _marker, ...rest } = attribs;
      return { tagName, attribs: rest };
    },
  },
  exclusiveFilter: (frame) => frame.tag === 'img' && !frame.attribs['data-file-id'] && !UPLOADABLE_SRC.test(frame.attribs['src'] ?? ''),
};

/** Sanitize HTML pasted into the rich-text editor. */
export const sanitizePastedHTML = (html: string, options: SanitizePastedHTMLOptions = {}): string => {
  return sanitizeHtml(removeInvalidColspans(html), {
    allowedTags: Object.keys(fullAllowedHtmlTags),
    allowedAttributes: fullAllowedHtmlTags,
    ...(options.keepPastedImages && pastedImageOptions),
  });
};
